import * as React from 'react';
import {
  DatePicker as RACDatePicker,
  DateInput,
  DateSegment,
  Button as RACButton,
  Popover,
  Dialog,
  Calendar,
  CalendarGrid as RACCalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
  CalendarHeading,
  type DateValue,
} from 'react-aria-components';
import {
  CalendarLine,
  LeftSmallLine,
  RightSmallLine,
} from '@mingcute/react';
import {
  CalendarDate,
  type DateDuration,
} from '@internationalized/date';

import { cn } from '../lib/cn';

/**
 * Klaret DatePicker (handoff §10.4).
 * Trigger = standard input with trailing calendar icon; segmented typed entry per
 * locale, keyboard-editable — never disabled.
 * Popover: r3, e2, 16 px padding; month header title-3 + chevrons; weekday row
 * footnote --tvl-ink-3; day grid 32 px cells, r2 hover; today = hairline ring;
 * selected = --tvl-accent + --tvl-on-accent; disabled days 35%.
 *
 * Exported names preserved from the original Radix/date-fns version:
 *   DatePicker, CalendarGrid, parseCzechDate
 */

// ---------------------------------------------------------------------------
// CalendarGrid — re-exported compound sub-component
// ---------------------------------------------------------------------------
export interface CalendarGridProps {
  /** Extra className for the grid wrapper. */
  className?: string;
  /** Offset for a second month panel (range pickers). */
  offset?: DateDuration;
  // Legacy props accepted but ignored — RAC Calendar drives all state:
  month?: Date;
  selected?: Date;
  rangeStart?: Date;
  rangeEnd?: Date;
  onSelect?: (d: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * CalendarGrid renders a 7-column month grid inside an RAC Calendar or
 * RangeCalendar context.
 */
export function CalendarGrid({ className, offset }: CalendarGridProps) {
  return (
    <div className={cn('mt-2', className)}>
      <RACCalendarGrid offset={offset}>
        <CalendarGridHeader>
          {(day) => (
            <CalendarHeaderCell className="pb-1 text-[11px] font-medium leading-4 text-[var(--tvl-ink-3)]">
              {day}
            </CalendarHeaderCell>
          )}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className={cn(
                'flex h-8 w-8 cursor-pointer select-none items-center justify-center',
                'rounded-[var(--tvl-r2)] text-sm tabular-nums text-[var(--tvl-ink)] outline-none',
                'hover:bg-[var(--tvl-wash-hover)]',
                'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                // today — hairline ring
                'data-[today]:ring-1 data-[today]:ring-[var(--tvl-hairline)]',
                // selected
                'data-[selected]:bg-[var(--tvl-accent)] data-[selected]:text-[var(--tvl-on-accent)] data-[selected]:hover:bg-[var(--tvl-accent-hover)]',
                // outside month
                'data-[outside-month]:opacity-35',
                // disabled / unavailable
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-35',
                'data-[unavailable]:pointer-events-none data-[unavailable]:opacity-35',
              )}
            />
          )}
        </CalendarGridBody>
      </RACCalendarGrid>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared calendar popover interior
// ---------------------------------------------------------------------------
interface CalendarPopoverBodyProps {
  minValue?: DateValue;
  maxValue?: DateValue;
}

function CalendarPopoverBody({ minValue, maxValue }: CalendarPopoverBodyProps) {
  return (
    <Calendar minValue={minValue} maxValue={maxValue} className="w-[17rem]">
      <div className="mb-1 flex items-center justify-between">
        <RACButton
          slot="previous"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-[var(--tvl-r2)]',
            'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] outline-none',
            'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
          )}
          aria-label="Previous month"
        >
          <LeftSmallLine size={16} aria-hidden />
        </RACButton>

        <CalendarHeading className="text-[16px] font-semibold leading-[22px] tracking-[-0.0025em] text-[var(--tvl-ink)]" />

        <RACButton
          slot="next"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-[var(--tvl-r2)]',
            'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] outline-none',
            'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
          )}
          aria-label="Next month"
        >
          <RightSmallLine size={16} aria-hidden />
        </RACButton>
      </div>

      <CalendarGrid />
    </Calendar>
  );
}

// ---------------------------------------------------------------------------
// DatePicker
// Use a plain interface (not extending RACDatePickerProps) to avoid the
// value-type conflict between Date and CalendarDate.
// ---------------------------------------------------------------------------
export interface DatePickerProps {
  value?: Date;
  onChange?: (d: Date | undefined) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

function toCalendarDate(d: Date): CalendarDate {
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function fromCalendarDate(cd: DateValue): Date {
  const c = cd as CalendarDate;
  return new Date(c.year, c.month - 1, c.day);
}

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder = 'dd.mm.rrrr',
      invalid,
      disabled,
      minDate,
      maxDate,
      className,
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
    },
    ref,
  ) => {
    const racValue = value ? toCalendarDate(value) : null;
    const racMin = minDate ? toCalendarDate(minDate) : undefined;
    const racMax = maxDate ? toCalendarDate(maxDate) : undefined;

    return (
      <RACDatePicker
        ref={ref}
        id={id}
        name={name}
        value={racValue}
        onChange={(v) => onChange?.(v ? fromCalendarDate(v) : undefined)}
        isDisabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        minValue={racMin}
        maxValue={racMax}
        className={cn('group flex w-full flex-col', className)}
      >
        {/* Input trigger */}
        <div
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-[var(--tvl-r2)]',
            // The date field is a composite of small dd/mm/yyyy segments, not an
            // <input>, so the global touch-target rule cannot reach it. Give the
            // field itself a 44px floor on touch and let DateInput fill it, so
            // each segment is tappable over the field's full height.
            '[@media(pointer:coarse)_and_(hover:none)]:min-h-11',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)] px-3',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'hover:border-[var(--tvl-ink-2)]',
            'group-focus-within:border-[var(--tvl-accent)]',
            'group-focus-within:shadow-[inset_0_0_0_1px_var(--tvl-accent)]',
            invalid && 'border-[var(--tvl-danger)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <DateInput className="flex h-full flex-1 items-center gap-0.5">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  // Segments stretch to the field's height so the tappable area
                  // is the full 44px on touch, not just the ~20px of glyph.
                  'flex h-full items-center',
                  'rounded px-0.5 text-sm tabular-nums text-[var(--tvl-ink)] outline-none',
                  'data-[placeholder]:text-[var(--tvl-ink-3)]',
                  'data-[focused]:bg-[var(--tvl-accent)] data-[focused]:text-[var(--tvl-on-accent)]',
                  'data-[type=literal]:text-[var(--tvl-ink-3)]',
                )}
              />
            )}
          </DateInput>
          <RACButton
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded outline-none',
              'text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink-2)]',
              'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
            )}
          >
            <CalendarLine size={16} aria-hidden />
          </RACButton>
        </div>

        {/* Calendar popover */}
        <Popover
          className={cn(
            'z-[var(--tvl-z-popover)] overflow-hidden rounded-[var(--tvl-r3)]',
            'border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
            'shadow-[var(--tvl-e2)] p-4',
            'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
            'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
          )}
          offset={4}
        >
          <Dialog className="outline-none">
            <CalendarPopoverBody minValue={racMin} maxValue={racMax} />
          </Dialog>
        </Popover>
      </RACDatePicker>
    );
  },
);
DatePicker.displayName = 'DatePicker';

// ---------------------------------------------------------------------------
// parseCzechDate — preserved utility
// ---------------------------------------------------------------------------
export function parseCzechDate(input: string): Date | undefined {
  // Accepts "dd.mm.yyyy" or "d.m.yyyy"
  const match = input.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) return undefined;
  const d = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const y = parseInt(match[3], 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;
  const result = new Date(y, m - 1, d);
  return isNaN(result.getTime()) ? undefined : result;
}
